import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { LayoutList, Kanban, Plus, Search, GripVertical, Calendar, FileText } from 'lucide-react';
import ApplicantDetail from './ApplicantDetail';
import NewApplicationDialog from './NewApplicationDialog';

// Generate Tailwind-safe badge styles from hex color
function badgeStyle(hex) {
  if (!hex) return {};
  return { backgroundColor: `${hex}18`, color: hex, borderColor: `${hex}30` };
}

export default function ApplicantsTab({ applications, jobs, refData, loading, apiCall, onRefresh }) {
  const stages = refData?.candidate_stages || [];
  const stageNames = useMemo(() => stages.map(s => s.name), [stages]);
  const stageMap = useMemo(() => {
    const m = {};
    stages.forEach(s => { m[s.name] = s; });
    return m;
  }, [stages]);
  const [view, setView] = useState('table');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [jobFilter, setJobFilter] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [draggedId, setDraggedId] = useState(null);

  const filtered = useMemo(() => applications.filter(a => {
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (jobFilter !== 'all' && a.job_id !== jobFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!a.candidate_name?.toLowerCase().includes(s) &&
          !a.candidate_email?.toLowerCase().includes(s) &&
          !a.job_title?.toLowerCase().includes(s)) return false;
    }
    return true;
  }), [applications, statusFilter, jobFilter, search]);

  const openDetail = async (app) => {
    try {
      const full = await apiCall('GET', `/recruitment/applications/${app.application_id}`);
      setSelectedApp(full);
      setDetailOpen(true);
    } catch (e) { toast.error(e.message); }
  };

  const handleStatusChange = async (appId, newStatus) => {
    try {
      await apiCall('PUT', `/recruitment/applications/${appId}`, { status: newStatus });
      toast.success(`Status → ${newStatus}`);
      onRefresh();
    } catch (e) { toast.error(e.message); }
  };

  const managerName = (pid) => {
    if (!pid || !refData?.members) return '';
    const m = refData.members.find(x => x.person_id === pid);
    return m?.name || '';
  };

  const salaryLabel = (a) => {
    if (!a.expected_salary_min && !a.expected_salary_max) return '--';
    const fmt = (v) => v ? `${(v/1000).toFixed(0)}k` : '';
    return `${a.expected_salary_currency || ''} ${fmt(a.expected_salary_min)}${a.expected_salary_max ? '–' + fmt(a.expected_salary_max) : ''}`;
  };

  // ── Kanban drag handlers ──
  const onDragStart = (e, appId) => {
    setDraggedId(appId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', appId);
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop = async (e, targetStatus) => {
    e.preventDefault();
    const appId = e.dataTransfer.getData('text/plain');
    if (!appId) return;
    const app = applications.find(a => a.application_id === appId);
    if (app && app.status !== targetStatus) {
      await handleStatusChange(appId, targetStatus);
    }
    setDraggedId(null);
  };

  // ── Kanban grouped — ALL stages always shown ──
  const grouped = useMemo(() => {
    const map = {};
    stageNames.forEach(s => { map[s] = []; });
    filtered.forEach(a => {
      if (map[a.status]) map[a.status].push(a);
      else if (stageNames.length > 0) map[stageNames[0]].push(a);
    });
    return map;
  }, [filtered, stageNames]);

  return (
    <div data-testid="applicants-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search applicants..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9" data-testid="applicants-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[170px] h-9" data-testid="applicants-status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {stageNames.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={jobFilter} onValueChange={setJobFilter}>
            <SelectTrigger className="w-[170px] h-9" data-testid="applicants-job-filter">
              <SelectValue placeholder="All Jobs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Jobs</SelectItem>
              {jobs.map(j => <SelectItem key={j.job_id} value={j.job_id}>{j.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border rounded-md overflow-hidden">
            <Button variant={view === 'table' ? 'secondary' : 'ghost'} size="sm" className="rounded-none h-9 px-3"
              onClick={() => setView('table')} data-testid="view-table-btn">
              <LayoutList className="w-4 h-4" />
            </Button>
            <Button variant={view === 'kanban' ? 'secondary' : 'ghost'} size="sm" className="rounded-none h-9 px-3"
              onClick={() => setView('kanban')} data-testid="view-kanban-btn">
              <Kanban className="w-4 h-4" />
            </Button>
          </div>
          <Button onClick={() => setNewDialogOpen(true)} size="sm" data-testid="add-applicant-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Applicant
          </Button>
        </div>
      </div>

      {/* Table View */}
      {view === 'table' && (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">Candidate</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Expected Salary</TableHead>
                <TableHead>Notice</TableHead>
                <TableHead>Manager</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-center">Mtg</TableHead>
                <TableHead className="text-center">Files</TableHead>
                <TableHead>Applied</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={11} className="text-center py-12 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={11} className="text-center py-12 text-muted-foreground">
                  {applications.length === 0 ? 'No applicants yet.' : 'No applicants match filters.'}
                </TableCell></TableRow>
              ) : filtered.map(app => (
                <TableRow key={app.application_id} className="cursor-pointer hover:bg-muted/50"
                  onClick={() => openDetail(app)} data-testid={`app-row-${app.application_id}`}>
                  <TableCell>
                    <div>
                      <span className="font-medium">{app.candidate_name || 'Unknown'}</span>
                      {app.candidate_email && <p className="text-xs text-muted-foreground">{app.candidate_email}</p>}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{app.job_title || '--'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{app.department_name || '--'}</TableCell>
                  <TableCell>
                    <Badge className="font-normal text-xs whitespace-nowrap border" style={badgeStyle(stageMap[app.status]?.color)}>{app.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right text-sm">{salaryLabel(app)}</TableCell>
                  <TableCell className="text-sm">{app.notice_period_text || '--'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{managerName(app.primary_manager_person_id) || '--'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{app.source_type || '--'}</TableCell>
                  <TableCell className="text-center"><Badge variant="outline" className="font-normal text-xs"><Calendar className="w-3 h-3 mr-0.5" />{app.meetings_count || 0}</Badge></TableCell>
                  <TableCell className="text-center"><Badge variant="outline" className="font-normal text-xs"><FileText className="w-3 h-3 mr-0.5" />{app.files_count || 0}</Badge></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{app.applied_at ? new Date(app.applied_at).toLocaleDateString() : '--'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Kanban View */}
      {view === 'kanban' && (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board" style={{ minHeight: 400 }}>
          {stageNames.map(status => {
            const stage = stageMap[status];
            return (
            <div key={status} className="flex-shrink-0 w-[260px]"
              onDragOver={onDragOver} onDrop={e => onDrop(e, status)}>
              <div className="rounded-lg border bg-muted/30 p-2 h-full min-h-[300px]">
                <div className="flex items-center justify-between mb-2 px-1">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage?.color || '#71717a' }} />
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{status}</span>
                  </div>
                  <Badge variant="secondary" className="text-xs font-normal h-5 min-w-[20px] flex items-center justify-center">{grouped[status]?.length || 0}</Badge>
                </div>
                <div className="space-y-2">
                  {(grouped[status] || []).map(app => (
                    <Card key={app.application_id}
                      draggable
                      onDragStart={e => onDragStart(e, app.application_id)}
                      onClick={() => openDetail(app)}
                      className={`p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow ${draggedId === app.application_id ? 'opacity-40' : ''}`}
                      data-testid={`kanban-card-${app.application_id}`}>
                      <div className="flex items-start gap-2">
                        <GripVertical className="w-3.5 h-3.5 mt-0.5 text-muted-foreground/50 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{app.candidate_name || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground truncate">{app.job_title || '--'}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-muted-foreground">{salaryLabel(app)}</span>
                            <span className="text-xs text-muted-foreground">{app.source_type || ''}</span>
                          </div>
                          {app.notice_period_text && (
                            <p className="text-xs text-muted-foreground mt-1">Notice: {app.notice_period_text}</p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">{managerName(app.primary_manager_person_id)}</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Detail Panel */}
      {detailOpen && selectedApp && (
        <ApplicantDetail
          app={selectedApp}
          refData={refData}
          apiCall={apiCall}
          onClose={() => { setDetailOpen(false); setSelectedApp(null); }}
          onRefresh={() => { onRefresh(); setDetailOpen(false); setSelectedApp(null); }}
          stages={stages}
          stageMap={stageMap}
          badgeStyle={badgeStyle}
        />
      )}

      {/* New Application Dialog */}
      <NewApplicationDialog
        open={newDialogOpen}
        onOpenChange={setNewDialogOpen}
        jobs={jobs}
        refData={refData}
        apiCall={apiCall}
        onCreated={() => { setNewDialogOpen(false); onRefresh(); }}
      />
    </div>
  );
}
